import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { api } from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import UserAvatar from '../components/UserAvatar';
import { AttachmentPreviewCard } from '../components/ContentAttachmentPicker';
import { 
  ArrowLeft, 
  Send, 
  MoreVertical,
  Check,
  CheckCheck,
  Loader2,
  Ban,
  Trash2,
  UserCheck,
  UserX,
  Music,
  Video,
  User,
  Paperclip,
  Phone,
  VideoIcon,
  PhoneOff,
  PhoneMissed,
  Bot
} from 'lucide-react';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import { toast } from 'sonner';
import { useCall } from '../context/CallContext';

export default function ConversationPage() {
  const { conversationId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const { startCall, callState } = useCall();

  const fetchConversation = useCallback(async () => {
    if (!token || !conversationId) return;

    try {
      const { ok, data, status } = await api.get(`/messages/conversations/${conversationId}`);

      if (ok) {
        setConversation(data.conversation);
        setMessages(data.messages || []);
      } else if (status === 404) {
        toast.error('Conversation not found');
        navigate('/messages');
      } else if (status === 403) {
        toast.error('You cannot access this conversation');
        navigate('/messages');
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [token, conversationId, navigate]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (!token || !conversationId) return;

    const interval = setInterval(() => {
      fetchConversation();
    }, 10000);

    return () => clearInterval(interval);
  }, [token, conversationId, fetchConversation]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || sending) return;

    setSending(true);
    const tempMessage = {
      message_id: `temp_${Date.now()}`,
      sender_id: user?.user_id,
      text: messageText,
      created_at: new Date().toISOString(),
      isOptimistic: true
    };

    // Optimistic update
    setMessages(prev => [...prev, tempMessage]);
    setMessageText('');

    try {
      const result = await api.post(`/messages/conversations/${conversationId}/messages`, { text: tempMessage.text });

      if (result.ok) {
        setMessages(prev => 
          prev.map(m => m.message_id === tempMessage.message_id 
            ? { ...result.data, sender_id: user?.user_id }
            : m
          )
        );
      } else {
        toast.error(result.error || 'Failed to send message');
        setMessages(prev => prev.filter(m => m.message_id !== tempMessage.message_id));
      }
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message');
      setMessages(prev => prev.filter(m => m.message_id !== tempMessage.message_id));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleAcceptRequest = async () => {
    setActionLoading(true);
    try {
      const { ok } = await api.patch(`/messages/conversations/${conversationId}/status`, { status: 'inbox' });

      if (ok) {
        toast.success('Message request accepted');
        setConversation(prev => ({ ...prev, status: 'inbox' }));
      } else {
        toast.error('Failed to accept request');
      }
    } catch (err) {
      toast.error('Failed to accept request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclineRequest = async () => {
    setActionLoading(true);
    try {
      const { ok } = await api.patch(`/messages/conversations/${conversationId}/status`, { status: 'declined' });

      if (ok) {
        toast.success('Message request declined');
        navigate('/messages?tab=requests');
      } else {
        toast.error('Failed to decline request');
      }
    } catch (err) {
      toast.error('Failed to decline request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlockUser = async () => {
    setActionLoading(true);
    try {
      const { ok } = await api.patch(`/messages/conversations/${conversationId}/status`, { status: 'blocked' });

      if (ok) {
        toast.success('User blocked');
        navigate('/messages');
      } else {
        toast.error('Failed to block user');
      }
    } catch (err) {
      toast.error('Failed to block user');
    } finally {
      setActionLoading(false);
      setShowBlockDialog(false);
    }
  };

  const handleDeleteConversation = async () => {
    setActionLoading(true);
    try {
      const { ok } = await api.delete(`/messages/conversations/${conversationId}`);

      if (ok) {
        toast.success('Conversation deleted');
        navigate('/messages');
      } else {
        toast.error('Failed to delete conversation');
      }
    } catch (err) {
      toast.error('Failed to delete conversation');
    } finally {
      setActionLoading(false);
    }
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'h:mm a')}`;
    }
    return format(date, 'MMM d, h:mm a');
  };

  const otherUser = conversation?.other_user || {};
  const isRequest = conversation?.status === 'request' && conversation?.initiated_by !== user?.user_id;
  const canSendMessage = conversation?.status === 'inbox' || conversation?.initiated_by === user?.user_id;

  if (loading) {
    return (
      <div className="py-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="py-20 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Conversation not found</h2>
          <Button onClick={() => navigate('/messages')}>Back to Messages</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background border-b px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/messages')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <Link 
            to={`/u/${otherUser.handle || otherUser.user_id}`}
            className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
          >
            <UserAvatar
              src={otherUser.avatar}
              name={otherUser.name || 'User'}
              size="md"
            />
            <div className="min-w-0">
              <h2 className="font-semibold truncate">{otherUser.name || 'Unknown User'}</h2>
              {otherUser.handle && (
                <p className="text-sm text-muted-foreground truncate">{otherUser.handle ? `@${otherUser.handle}` : ""}</p>
              )}
            </div>
          </Link>

          {/* Call buttons */}
          {canSendMessage && callState === 'idle' && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => startCall(conversationId, otherUser.user_id, otherUser.name, otherUser.avatar, 'voice')}
                data-testid="voice-call-btn"
                title="Voice call"
              >
                <Phone className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => startCall(conversationId, otherUser.user_id, otherUser.name, otherUser.avatar, 'video')}
                data-testid="video-call-btn"
                title="Video call"
              >
                <VideoIcon className="w-5 h-5" />
              </Button>
            </div>
          )}

          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowOptions(!showOptions)}
              data-testid="options-button"
            >
              <MoreVertical className="w-5 h-5" />
            </Button>

            {showOptions && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-popover border rounded-lg shadow-lg py-1 z-20">
                <button
                  onClick={() => {
                    setShowBlockDialog(true);
                    setShowOptions(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 text-destructive"
                >
                  <Ban className="w-4 h-4" />
                  Block User
                </button>
                <button
                  onClick={() => {
                    handleDeleteConversation();
                    setShowOptions(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Conversation
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Banner */}
      {isRequest && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-3">
          <div className="max-w-2xl mx-auto">
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
              <strong>{otherUser.name}</strong> wants to send you a message. Accept to start chatting or decline to remove this request.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleAcceptRequest}
                disabled={actionLoading}
                className="flex items-center gap-2"
                data-testid="accept-request-btn"
              >
                <UserCheck className="w-4 h-4" />
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDeclineRequest}
                disabled={actionLoading}
                className="flex items-center gap-2"
                data-testid="decline-request-btn"
              >
                <UserX className="w-4 h-4" />
                Decline
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="px-4 py-4 min-h-[400px]">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No messages yet. Say hello!</p>
            </div>
          ) : (
            messages.map((message, idx) => (
              <MessageBubble
                key={message.message_id}
                message={message}
                isOwn={message.sender_id === user?.user_id}
                formatTime={formatMessageTime}
                showAvatar={idx === 0 || messages[idx - 1]?.sender_id !== message.sender_id}
                otherUser={otherUser}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="sticky bottom-0 bg-background border-t px-4 py-3 z-20">
        <div className="max-w-2xl mx-auto">
          {canSendMessage ? (
            <form onSubmit={sendMessage} className="flex gap-2">
              <Input
                ref={inputRef}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type a message..."
                className="flex-1"
                disabled={sending}
                data-testid="message-input"
              />
              <Button
                type="submit"
                disabled={!messageText.trim() || sending}
                data-testid="send-message-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          ) : (
            <p className="text-center text-sm text-muted-foreground">
              Accept this message request to reply
            </p>
          )}
        </div>
      </div>

      {/* Block Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block {otherUser.name}?</DialogTitle>
            <DialogDescription>
              They won't be able to message you or see your profile. You can unblock them later from settings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBlockUser}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Block'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessageBubble({ message, isOwn, formatTime, showAvatar, otherUser }) {
  // System messages (call events)
  if (message.is_system) {
    const isCallEvent = message.system_event?.startsWith('call_');
    const isMissed = message.system_event === 'call_missed';
    const isDeclined = message.system_event === 'call_declined';
    const isVideoCall = message.call_type === 'video';

    return (
      <div className="flex justify-center py-2" data-testid={`system-msg-${message.message_id}`}>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs ${
          isMissed || isDeclined ? 'bg-red-500/10 text-red-400' : 'bg-muted text-muted-foreground'
        }`}>
          {isCallEvent && (
            isMissed ? <PhoneMissed className="w-3.5 h-3.5" /> :
            isVideoCall ? <VideoIcon className="w-3.5 h-3.5" /> :
            <Phone className="w-3.5 h-3.5" />
          )}
          <span>{message.text}</span>
          <span className="opacity-60">{formatTime(message.created_at)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}>
      {!isOwn && showAvatar && (
        <UserAvatar
          src={otherUser.avatar}
          name={otherUser.name || 'User'}
          size="sm"
        />
      )}
      {!isOwn && !showAvatar && <div className="w-8" />}
      
      <div className={`max-w-[70%] ${isOwn ? 'order-first' : ''}`}>
        <div
          className={`rounded-2xl px-4 py-2 ${
            isOwn
              ? 'bg-primary text-primary-foreground rounded-br-sm'
              : 'bg-muted rounded-bl-sm'
          } ${message.isOptimistic ? 'opacity-70' : ''}`}
          data-testid={`message-${message.message_id}`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
          
          {/* Message Attachment */}
          {message.attachment_type && message.attachment_id && (
            <div className="mt-2">
              <AttachmentPreviewCard
                attachment={{
                  attachmentType: message.attachment_type,
                  attachmentId: message.attachment_id,
                  attachmentTitle: message.attachment_metadata?.title || 'Attached Content',
                  attachmentCoverUrl: message.attachment_metadata?.cover_url,
                  attachmentDuration: message.attachment_metadata?.duration,
                  attachmentOwnerName: message.attachment_metadata?.owner_name
                }}
                onRemove={null}
                compact={true}
              />
            </div>
          )}
        </div>
        
        <div className={`flex items-center gap-1 mt-1 text-xs text-muted-foreground ${isOwn ? 'justify-end' : ''}`}>
          {message.is_ai_twin && (
            <span className="inline-flex items-center gap-0.5 text-violet-400 mr-1" data-testid="ai-twin-indicator">
              <Bot className="w-3 h-3" /> AI Twin
            </span>
          )}
          <span>{formatTime(message.created_at)}</span>
          {isOwn && !message.isOptimistic && (
            message.read ? (
              <CheckCheck className="w-3 h-3 text-primary" />
            ) : (
              <Check className="w-3 h-3" />
            )
          )}
          {message.isOptimistic && (
            <Loader2 className="w-3 h-3 animate-spin" />
          )}
        </div>
      </div>
    </div>
  );
}
